import { getRouteLocationDisplay } from 'shared/utils/routeLocationDisplay';

/**
 * Генерация alt для изображений маршрута/видео по правилам SEO_alt.md.
 * Используется для атрибута alt у img и для description в ImageObject (JSON-LD).
 *
 * @param {object} route - Маршрут (path, city_or_region, country, route_type, title)
 * @param {object} options - { routeStartPlaceName?, routeEndPlaceName?, locale? }
 * @returns {string} Строка для alt / description
 */
export const getRouteImageAlt = (route, options = {}) => {
  if (!route || typeof route !== 'object') return '';

  const locale = (options.locale || '').split('-')[0] || 'en';
  const { country, cityOrRegion } = getRouteLocationDisplay(route, options.locale || locale);
  const startName = options.routeStartPlaceName != null ? String(options.routeStartPlaceName).trim() : '';
  const endName = options.routeEndPlaceName != null ? String(options.routeEndPlaceName).trim() : '';
  const routeType = route.route_type || route.routeType || 'route';

  // Одна точка (место) → "{city}, {country} city travel video"
  if (routeType === 'place') {
    const city = cityOrRegion || (route.title && String(route.title).trim()) || '';
    if (city && country) return `${city}, ${country} city travel video`;
    if (country) return `${city || 'Place'}, ${country} city travel video`;
    return city ? `${city} city travel video` : 'Travel video';
  }

  // Разные начало и конец → "Road from {start} to {end}, {country} – travel video route"
  const startEndDiffer = startName && endName && startName.toLowerCase() !== endName.toLowerCase();
  if (startEndDiffer) {
    if (country) return `Road from ${startName} to ${endName}, ${country} – travel video route`;
    return `Road from ${startName} to ${endName} – travel video route`;
  }

  // Несколько точек / один регион → "{POI} in {region}, {country} – travel video"
  let poi = '';
  if (route.path) {
    try {
      const path = typeof route.path === 'string' ? JSON.parse(route.path || '[]') : route.path;
      if (Array.isArray(path) && path.length > 0) {
        for (const p of path) {
          const tr = p.point_title_translations && typeof p.point_title_translations === 'object'
            ? (p.point_title_translations[locale] || p.point_title_translations.en || p.point_title_translations.ru)
            : null;
          const name = (tr && String(tr).trim()) || (p.pointTitle && String(p.pointTitle).trim()) || (p.point_title && String(p.point_title).trim());
          if (name) {
            poi = name;
            break;
          }
        }
        if (!poi && path.length > 0) {
          const p = path[0];
          const cityPt = (p.cityOrRegion || p.city_or_region) != null ? String(p.cityOrRegion || p.city_or_region).trim() : '';
          const countryPt = (p.country != null) ? String(p.country).trim() : '';
          if (cityPt || countryPt) poi = [cityPt, countryPt].filter(Boolean).join(', ');
        }
      }
    } catch (_) { /* ignore */ }
  }
  if (!poi) poi = cityOrRegion || (route.title && String(route.title).trim()) || '';

  const region = cityOrRegion || '';
  if (poi && country) {
    return region ? `${poi} in ${region}, ${country} – travel video` : `${poi} in ${country} – travel video`;
  }
  if (country) return region ? `${region}, ${country} – travel video` : `${country} – travel video`;
  return poi ? `${poi} – travel video` : 'Travel video';
};
